import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { HoldingsBenchmarkCategory, Portfolio } from 'venn-api';
import { getHoldingsCategories } from 'venn-api';
import { createFixedStrategiesKey, useHasFF, isStrategyNode } from 'venn-utils';
import { flatMap, every, isEmpty } from 'lodash';

export interface ConstituentPortfoliosResult {
  constituents: Portfolio[] | null;
  isLoading: boolean;
}

/**
 * Hook to determine constituent portfolios when adding investments in the allocator panel.
 * Fetches the asset class taxonomy and uses it to distinguish between actual portfolio
 * constituents and organizational grouping nodes (household/client) or asset class nodes.
 */
export const useConstituentPortfolios = (portfolio: Portfolio | undefined): ConstituentPortfoliosResult => {
  const hasRCMPortfolioAllocatorStrategyEnforcement = useHasFF('rcm_portfolio_strategy_enforcement_ff');

  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery<HoldingsBenchmarkCategory[]>(
    createFixedStrategiesKey('ASSET', 'WORKSPACE'),
    async () => (await getHoldingsCategories('ASSET', 'WORKSPACE')).content || [],
    {
      staleTime: 10 * 60 * 1000,
      keepPreviousData: true,
      enabled: hasRCMPortfolioAllocatorStrategyEnforcement,
    },
  );

  const strategyNames = useMemo(() => {
    if (!hasRCMPortfolioAllocatorStrategyEnforcement || categories.length === 0) {
      return new Set<string>();
    }

    const collectNames = (cats: HoldingsBenchmarkCategory[]): string[] =>
      flatMap(cats, (cat) => [
        ...(cat.categoryName ? [cat.categoryName] : []),
        ...(cat.children ? collectNames(cat.children) : []),
      ]);

    return new Set(collectNames(categories));
  }, [categories, hasRCMPortfolioAllocatorStrategyEnforcement]);

  const constituents = useMemo(() => {
    if (!portfolio || !isStrategyNode(portfolio)) {
      return null;
    }

    // Demo or master portfolios should not show the constituent selector
    if (portfolio.demo || portfolio.master) {
      return null;
    }

    const children = portfolio.children || [];
    const strategyChildren = children.filter(isStrategyNode);

    if (isEmpty(strategyChildren)) {
      return null;
    }

    // Recursively find the actual constituent portfolios by traversing through the tree
    const findConstituents = (nodes: Portfolio[]): Portfolio[] =>
      flatMap(nodes, (node) => {
        // Skip any known strategies (e.g., "Equity", "Fixed Income")
        if (strategyNames.has(node.name)) {
          return [];
        }

        const nodeStrategyChildren = (node.children || []).filter(isStrategyNode);

        // This is a constituent portfolio if:
        // 1. It has no strategy node children (i.e. "leaf" portfolio), OR
        // 2. All its strategy node children are known asset classes
        const hasOnlyAssetClassChildren =
          nodeStrategyChildren.length > 0 && every(nodeStrategyChildren, (child) => strategyNames.has(child.name));

        const isConstituent = isEmpty(nodeStrategyChildren) || hasOnlyAssetClassChildren;

        if (isConstituent) {
          return [node];
        }

        // Otherwise, this is a grouping node (e.g. household/client) - recurse into children
        return findConstituents(nodeStrategyChildren);
      });

    const foundConstituents = findConstituents(strategyChildren);

    // Only show the selector if there are multiple constituents
    return foundConstituents.length > 1 ? foundConstituents : null;
  }, [portfolio, strategyNames]);

  return { constituents, isLoading: hasRCMPortfolioAllocatorStrategyEnforcement && isCategoriesLoading };
};
